import { useQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import { getOrderDetailsById } from '@atlassian/temp-proxies';
import {
  FetchOrderDetailForTransactionAccountQuery,
  FetchOrderDetailForTransactionAccountQueryVariables,
  Order,
} from '@css-xp/graphtypes/src/generated-types-new';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import {
  AppContent,
  AppPage,
  ErrorComponents,
  errorNotificationBuilder,
  MoreActionsItems,
  MoreActionsV2,
  NotificationTypes,
  useFeatureToggle,
  useNotification,
  useRecentlyViewed,
} from '@css-xp/base';
import { PageBackground, PageBackgroundV2 } from '@css-xp/page-background';
import { Background } from '@css-xp/page-background/src/background-image';
import {
  routePaths,
  ENABLE_DESIGN_ITERATION_1,
  RecentlyViewedEntityEnum,
  STATSIG_FEATURE_GATES,
} from '@css-xp/ppc-constants';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import { OrderHistoryTable, OrderHistoryTableColumns } from '@css-xp/ppc-order-history-table';
import { failureMessage, ScreenEventAnalytics } from '@css-xp/ppc-common';
import { DarkLoadingButton } from '@css-xp/ppc-dark-button';
import { downloadAsJSON, getEntityName } from '@css-xp/ppc-utils';
import { OrderDetailsPageLoader } from '@css-xp/ppc-page-loaders';
import { OrderDetailsSummarySection } from '../components/order-details-summary-section';
import { fetchOrderDetailForTransactionAccount } from '../queries/fetch-order-detail-for-transaction-account';
import './order-details-page.scss';

const errorNotification = errorNotificationBuilder()
  .isVersion(NotificationTypes.FLAG)
  .withHeader('Export unsuccessful')
  .withMessage(<>We are unable to export the order details - {failureMessage()}</>)
  .withId('export-order-details-error')
  .withDismissAfter(5000)
  .build();

const exportAsJson = async (orderId: string, transactionAccountId: string, fileName: string) => {
  const data = await getOrderDetailsById(orderId, transactionAccountId);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  downloadAsJSON(data as any, fileName);
};

export const OrderDetailsPage = (): JSX.Element | null => {
  const [isBeingExported, setIsBeingExported] = React.useState(false);
  const { orderId, txaId } = useParams<{ orderId: string; txaId: string }>();
  const history = useHistory();
  const orderFilter = [
    {
      key: 'id',
      value: orderId,
    },
  ];

  const { sendErrorNotification, setNotificationSent } = useNotification();
  const [isEnabled] = useFeatureToggle();
  const { updateRecentlyViewed } = useRecentlyViewed();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);

  const { data, loading, error } = useQuery<
    FetchOrderDetailForTransactionAccountQuery,
    FetchOrderDetailForTransactionAccountQueryVariables
  >(fetchOrderDetailForTransactionAccount, {
    variables: {
      transactionAccountId: txaId,
      filterOptions: orderFilter,
      fetchIsExternallyBilled: true,
    },
    displayName: 'fetchOrderForTransactionAccount',
    skip: !txaId || !orderId,
  });
  const totalOrderCount = data?.transactionAccount?.orders?.totalCount;
  const hasNoOrderForTXA = totalOrderCount === 0;
  const isExportingCSVHidden = isDesignIteration1Enabled;

  useEffect(() => {
    const order = data?.transactionAccount?.orders?.nodes?.[0] as Order;
    updateRecentlyViewed({
      entityName: getEntityName(RecentlyViewedEntityEnum.ORDER, txaId),
      metadata: {
        id: orderId,
        orderNumber: order?.orderNumber,
        companyName: order?.billTo?.billToCompanyName,
      },
    });
  }, [data]);

  if (hasNoOrderForTXA) {
    // Redirect to not found page if no order is found for the TXA
    history.push(routePaths.notFound);

    // Return null to make sure that the order details page is not rendered causing any undefined errors
    return null;
  }

  if (loading) {
    if (isDesignIteration1Enabled) {
      return <OrderDetailsPageLoader />;
    }

    return <LoadingIndicator />;
  }

  if (error) {
    const GenericError = withErrorWrapper(ErrorComponents.GenericErrorPage);
    return <GenericError error={error} />;
  }

  if (!data) {
    const NotFound = withErrorWrapper(ErrorComponents.NotFound);
    return <NotFound />;
  }

  const orders = data?.transactionAccount?.orders?.nodes ?? [];
  const order = orders[0] as Order;
  const orderNumber = order.orderNumber;
  const orderHasManuallyBilledItems = order?.orderLineItems?.nodes.some((lineItem) => lineItem?.isExternallyBilled);

  const getPageHeading = () => {
    return (
      <div className="page-heading">
        <h1>{orderNumber}</h1>
      </div>
    );
  };

  const renderActions = () => {
    return (
      <DarkLoadingButton
        appearance="primary"
        isLoading={isBeingExported}
        onClick={async () => {
          try {
            setIsBeingExported(true);
            await exportAsJson(orderId, txaId, `order-${orderNumber}`);
          } catch {
            sendErrorNotification(errorNotification);
            setNotificationSent(true);
          } finally {
            setIsBeingExported(false);
          }
        }}
        testId="export-order-as-json"
      >
        Export as JSON
      </DarkLoadingButton>
    );
  };

  const columnsToShow = [
    OrderHistoryTableColumns.SUBSCRIPTION,
    OrderHistoryTableColumns.ORDER_SUMMARY,
    OrderHistoryTableColumns.SITE,
  ];

  const renderAppPage = () => {
    return (
      <AppPage className="app-page-v2 ppc-order-details-page">
        <ScreenEventAnalytics
          event={{
            name: AnalyticsEventSource.PARTNER_ORDER_DETAIL_SCREEN_BY_ID,
            attributes: {},
          }}
        />
        <AppContent>
          <section>
            <OrderDetailsSummarySection
              order={order}
              txaId={txaId}
              isVersion2={isDesignIteration1Enabled}
              orderHasManuallyBilledItems={orderHasManuallyBilledItems}
              isManualBillingSupportEnabled={true}
            />
          </section>
          <div className={'order-details-table-container'}>
            <OrderHistoryTable
              label="Order history"
              transactionAccountId={txaId}
              filters={orderFilter}
              shouldNotGroupRows={true}
              isExportingCSVHidden={isExportingCSVHidden}
              columnsToShow={isDesignIteration1Enabled ? columnsToShow : undefined}
              shouldEnableFrontEndSearch={isDesignIteration1Enabled}
            />
          </div>
        </AppContent>
      </AppPage>
    );
  };

  if (isDesignIteration1Enabled) {
    const breadcrumbsItems = [
      {
        title: 'Home',
        href: routePaths.home,
      },
      {
        title: 'Orders',
        href: `${routePaths.details}#order-history`,
      },
      {
        title: orderNumber ?? 'Details',
        href: routePaths.orderDetails,
      },
    ];

    const orderDetailsPageActions: MoreActionsItems[] = [
      {
        id: 'orders-export-json',
        content: 'Export as JSON',
        action: async () => {
          try {
            setIsBeingExported(true);
            await exportAsJson(orderId, txaId, `order-${orderNumber}`);
          } catch {
            sendErrorNotification(errorNotification);
            setNotificationSent(true);
          } finally {
            setIsBeingExported(false);
          }
        },
      },
    ];

    const renderActionV2 = () => {
      return (
        <MoreActionsV2
          actions={orderDetailsPageActions}
          trigger="Manage"
          ariaLabel="menu"
          isLoading={isBeingExported}
          appearance="default"
          testId="order-manage-dropdown-trigger"
        />
      );
    };

    return (
      <PageBackgroundV2 heading={orderNumber} actions={renderActionV2()} breadcrumbsProps={{ items: breadcrumbsItems }}>
        {renderAppPage()}
      </PageBackgroundV2>
    );
  }

  return (
    <PageBackground
      heading={getPageHeading()}
      imageKey={Background.PPC_HEADER_BACKGROUND}
      className="page-background ppc-order-details-background"
      isBackButtonEnabled={true}
      actions={renderActions()}
    >
      {renderAppPage()}
    </PageBackground>
  );
};
